import React, { useState, useMemo } from 'react';
import { Table, Form, Row, Col } from 'react-bootstrap';
import IntegerFmt from '../../components/common/typeFormatting/IntegerFmt';
import Dollars from '../../components/common/typeFormatting/Dollars';
import DateFmt from '../../components/common/typeFormatting/Date';
import { EmployeeOptionsStatement } from '../types/employee-statement';
import { transformToActivityEvents, filterEventsByYear, filterEventsByType } from '../services/activity-transformer';
import '../styles/options-tables.css';

interface ActivityTableProps {
  data: EmployeeOptionsStatement;
}

export const ActivityTable: React.FC<ActivityTableProps> = ({ data }) => {
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedEventType, setSelectedEventType] = useState('all');
  const [selectedAgreement, setSelectedAgreement] = useState('all');

  // Transform data into activity events
  const activityData = useMemo(() => transformToActivityEvents(data), [data]);

  // Get unique option agreement descriptions for filter
  const uniqueAgreements = useMemo(() => {
    const agreementsSet = new Set<string>();
    activityData.events.forEach(event => {
      agreementsSet.add(event.optionAgreementDescription);
    });
    return Array.from(agreementsSet).sort();
  }, [activityData.events]);

  // Apply filters
  const filteredEvents = useMemo(() => {
    let events = activityData.events;
    events = filterEventsByYear(events, selectedYear);
    events = filterEventsByType(events, selectedEventType);

    // Filter by option agreement
    if (selectedAgreement !== 'all') {
      events = events.filter(event => event.optionAgreementDescription === selectedAgreement);
    }

    return events;
  }, [activityData.events, selectedYear, selectedEventType, selectedAgreement]);

  // Calculate totals from filtered events
  const totals = useMemo(() => {
    const hasAnyValue = filteredEvents.some(event => event.value !== null);
    const totalValue = hasAnyValue ? filteredEvents.reduce((sum, event) => sum + (event.value || 0), 0) : null;
    return { totalValue };
  }, [filteredEvents]);

  const getBadgeStyle = (type: string) => {
    switch (type) {
      case 'Grant':
        return {
          background: '#f3e5f5',
          color: '#7b1fa2',
          icon: '◈'
        };
      case 'Vesting':
        return {
          background: '#e8f5e9',
          color: '#388e3c',
          icon: '↗︎'
        };
      case 'Exercise':
        return {
          background: '#e3f2fd',
          color: '#1976d2',
          icon: '$'
        };
      default:
        return {
          background: '#f5f5f5',
          color: '#666',
          icon: ''
        };
    }
  };

  return (
    <div>
      {/* Filters */}
      <Row className="mb-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Option Agreement</Form.Label>
            <Form.Control
              as="select"
              value={selectedAgreement}
              onChange={(e) => setSelectedAgreement(e.target.value)}
            >
              <option value="all">All Agreements</option>
              {uniqueAgreements.map(agreement => (
                <option key={agreement} value={agreement}>{agreement}</option>
              ))}
            </Form.Control>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Event Type</Form.Label>
            <Form.Control
              as="select"
              value={selectedEventType}
              onChange={(e) => setSelectedEventType(e.target.value)}
            >
              <option value="all">All Events</option>
              <option value="grant">Grants</option>
              <option value="vesting">Vesting</option>
              <option value="exercise">Exercises</option>
            </Form.Control>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Year</Form.Label>
            <Form.Control
              as="select"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              <option value="all">All Years</option>
              {activityData.availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </Form.Control>
          </Form.Group>
        </Col>
        <Col md={2} className="d-flex align-items-end justify-content-end">
          <div style={{ color: '#546e7a', fontSize: '0.9rem' }}>
            {filteredEvents.length} {filteredEvents.length === 1 ? 'event' : 'events'}
          </div>
        </Col>
      </Row>

      {/* Events Table */}
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Table hover responsive className="options-table" style={{ width: 'auto' }}>
          <thead>
            <tr>
              <th style={{ width: '130px' }}>Date</th>
              <th style={{ width: '100px' }}>Type</th>
              <th style={{ width: '300px' }}>Description</th>
              <th className="header-numeric-col">Shares</th>
              <th className="header-numeric-col">Stock Price</th>
              <th className="header-numeric-col">Value</th>
            </tr>
          </thead>
          <tbody>
            {filteredEvents.map((event, index) => {
              const badgeStyle = getBadgeStyle(event.type);
              return (
                <tr key={index}>
                  <td>
                    <DateFmt>{event.date}</DateFmt>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontSize: '1rem', color: badgeStyle.color, fontWeight: 'bold' }}>{badgeStyle.icon}</span>
                      <span
                        style={{
                          padding: '0.15rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          background: badgeStyle.background,
                          color: badgeStyle.color
                        }}
                      >
                        {event.type}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div>{event.description}</div>
                    {event.additionalDescription && (
                      <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem', whiteSpace: 'pre-line' }}>
                        {event.additionalDescription}
                      </div>
                    )}
                  </td>
                  <td>
                    <IntegerFmt>{event.shares}</IntegerFmt>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {event.type === 'Vesting' ? '—' : <Dollars>{event.stockPrice}</Dollars>}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {event.value !== null ? <Dollars>{event.value}</Dollars> : '—'}
                  </td>
                </tr>
              );
            })}
            {/* Totals Row */}
            <tr style={{ fontWeight: 600, borderTop: '2px solid #dee2e6' }}>
              <td></td>
              <td></td>
              <td>Total</td>
              <td></td>
              <td></td>
              <td style={{ textAlign: 'right' }}>
                {totals.totalValue !== null ? <Dollars>{totals.totalValue}</Dollars> : '—'}
              </td>
            </tr>
          </tbody>
        </Table>
      </div>
    </div>
  );
};
